import { useState } from "react";
import { ArrowRight, X, CheckCircle2 } from "lucide-react";

export default function ConsultationDialog({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", clinic: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  if (!open) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!form.email.trim()) err.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email";
    if (!form.message.trim()) err.message = "Message is required";
    return err;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }
    // Save submission locally
    const existing = JSON.parse(localStorage.getItem("tinde_consultations") || "[]");
    existing.push({ ...form, submittedAt: new Date().toISOString() });
    localStorage.setItem("tinde_consultations", JSON.stringify(existing));
    setSubmitted(true);
  };

  const handleClose = () => {
    setSubmitted(false);
    setForm({ name: "", email: "", phone: "", clinic: "", message: "" });
    setErrors({});
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative bg-page w-full max-w-xl rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          aria-label="Close"
          onClick={handleClose}
          className="absolute top-5 right-5 text-[#4a4a4a] hover:text-[#b89968] transition-colors"
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div className="px-10 py-16 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6 bg-[#f3ece1]">
              <CheckCircle2 size={28} className="tinde-gold" strokeWidth={1.4} />
            </div>
            <h3 className="font-serif text-3xl text-[#1a1a1a] mb-3">Thank You.</h3>
            <p className="text-[#5a5a5a] leading-relaxed max-w-sm mx-auto">
              The consultation request has been received. A member of the team will reach out shortly.
            </p>
            <button
              onClick={handleClose}
              className="btn-dark mt-10 inline-flex items-center gap-4 px-8 py-3 text-[10px] tracking-[0.3em] uppercase"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="px-8 md:px-10 py-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px tinde-gold-bg"></span>
              <span className="text-[10px] tracking-[0.3em] tinde-gold uppercase">Get in Touch</span>
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] mb-2">Book a Consultation</h3>
            <p className="text-sm text-[#5a5a5a] mb-8">
              Share a few details and the team will follow up within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-[#3a3a3a] mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full border-b border-[#d4d4d4] bg-transparent py-2 text-sm focus:outline-none focus:border-[#b89968] transition-colors"
                    placeholder="Jane Doe"
                  />
                  {errors.name && <p className="text-[11px] text-red-600 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-[#3a3a3a] mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border-b border-[#d4d4d4] bg-transparent py-2 text-sm focus:outline-none focus:border-[#b89968] transition-colors"
                    placeholder="name@email.com"
                  />
                  {errors.email && <p className="text-[11px] text-red-600 mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-[#3a3a3a] mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full border-b border-[#d4d4d4] bg-transparent py-2 text-sm focus:outline-none focus:border-[#b89968] transition-colors"
                    placeholder="+966 ..."
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-[#3a3a3a] mb-2">Clinic / Brand</label>
                  <input
                    type="text"
                    name="clinic"
                    value={form.clinic}
                    onChange={handleChange}
                    className="w-full border-b border-[#d4d4d4] bg-transparent py-2 text-sm focus:outline-none focus:border-[#b89968] transition-colors"
                    placeholder="Clinic name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#3a3a3a] mb-2">Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border-b border-[#d4d4d4] bg-transparent py-2 text-sm focus:outline-none focus:border-[#b89968] transition-colors resize-none"
                  placeholder="How can we help?"
                />
                {errors.message && <p className="text-[11px] text-red-600 mt-1">{errors.message}</p>}
              </div>

              <button
                type="submit"
                className="btn-dark group mt-4 inline-flex items-center gap-6 px-8 py-4 text-[10px] tracking-[0.3em] uppercase"
              >
                Send Request
                <ArrowRight size={14} className="hover-arrow" />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
